import type { ComplexityArgumentSpec } from '../model.js';

export function complexityArgumentFixture(
  width = 640,
  stress = false,
  focusStepId?: string,
): ComplexityArgumentSpec {
  return {
    id: stress ? 'complexity-argument-stress' : 'complexity-argument',
    family: 'complexity-argument',
    view: 'annotated-argument',
    viewport: { width, height: stress ? 620 : 540 },
    claim: stress
      ? 'The total work performed by the triangular pair-enumeration loop grows quadratically: T(n) ∈ Θ(n²)'
      : 'The triangular loop performs Θ(n²) work',
    steps: stress
      ? [
          {
            id: 'count-iterations',
            expression: 'T(n) = Σᵢ₌₁ⁿ Σⱼ₌₁ⁱ 1',
            rationale: 'The inner body executes exactly once for every ordered pair whose second index does not exceed the first.',
            kind: 'given',
          },
          {
            id: 'collapse-inner-sum',
            expression: 'T(n) = Σᵢ₌₁ⁿ i',
            rationale: 'For a fixed outer index i, the inner summation contributes i constant-time operations.',
            kind: 'transform',
          },
          {
            id: 'apply-series-identity',
            expression: 'T(n) = n(n + 1) / 2',
            rationale: 'The closed form of the first n positive integers is n multiplied by its successor, divided by two.',
            kind: 'transform',
          },
          {
            id: 'distribute-product',
            expression: 'T(n) = (n² + n) / 2',
            rationale: 'Distributing n over the parenthesized sum preserves equality and exposes the polynomial terms.',
            kind: 'transform',
          },
          {
            id: 'separate-coefficients',
            expression: 'T(n) = ½n² + ½n',
            rationale: 'Dividing both polynomial terms by two makes their coefficients explicit without changing the growth function.',
            kind: 'transform',
          },
          {
            id: 'bound-polynomial',
            expression: '½n² ≤ T(n) ≤ n² for every n ≥ 1',
            rationale: 'The positive linear term gives the lower bound, while n is at most n squared for all positive n.',
            kind: 'transform',
          },
          {
            id: 'state-tight-bound',
            expression: 'T(n) ∈ Θ(n²)',
            rationale: 'Matching constant-factor upper and lower quadratic bounds establish the tight asymptotic class.',
            kind: 'conclusion',
          },
        ]
      : [
          {
            id: 'count-iterations',
            expression: 'T(n) = Σᵢ₌₁ⁿ i',
            rationale: 'Outer iteration i performs i units of work.',
            kind: 'given',
          },
          {
            id: 'apply-series-identity',
            expression: 'T(n) = n(n + 1) / 2',
            rationale: 'Use the closed form for the first n integers.',
            kind: 'transform',
          },
          {
            id: 'expand-polynomial',
            expression: 'T(n) = ½n² + ½n',
            rationale: 'Expand the product and divide each term by two.',
            kind: 'transform',
          },
          {
            id: 'identify-dominant-term',
            expression: 'T(n) ∈ Θ(n²)',
            rationale: 'Constant factors and lower-order terms do not change asymptotic growth.',
            kind: 'conclusion',
          },
        ],
    ...(focusStepId ? { focusStepId } : {}),
    provenance: 'Worked triangular-loop complexity derivation · exact operation count followed by a tight asymptotic bound',
  };
}

export const defaultComplexityFocus = 'identify-dominant-term';
export const complexityGuidedTargets = {
  given: 'argument.step.count-iterations',
  identity: 'argument.step.apply-series-identity',
  dominantTerm: 'argument.step.identify-dominant-term',
} as const;
